import { ratingScale, reflectionScale, type RatingLevel } from "../../utils/appraisal";

/** A rating with every point named, so a rating means the same thing to
 *  everyone.
 *
 *  - `scale="company"` (default): the company 1–5 scale the manager gives.
 *  - `scale="self"` / `"colleague"`: four words a person uses to reflect on
 *    their own work (or a colleague's). No numbers are shown, so it is never
 *    read as the company rating.
 *
 *  `size="lg"` is used for the overall rating. */
export default function RatingScale({
  value,
  onChange,
  disabled,
  size = "md",
  name,
  scale = "company",
}: {
  value: number | null;
  onChange: (v: number) => void;
  disabled?: boolean;
  size?: "md" | "lg";
  /** Accessible label for the group. */
  name?: string;
  scale?: "company" | "self" | "colleague";
}) {
  const words = scale !== "company";
  const levels: RatingLevel[] = words ? reflectionScale(scale === "colleague" ? "colleague" : "self") : ratingScale();
  const chosen = levels.find((l) => l.value === value);
  const big = size === "lg";
  const cols = words ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-5";
  return (
    <div>
      <div role="radiogroup" aria-label={name ?? "Rating"} className={`grid ${cols} gap-1.5`}>
        {levels.map((l) => {
          const on = value === l.value;
          return (
            <button
              key={l.value}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={disabled}
              title={words ? `${l.label}: ${l.hint}` : `${l.value} · ${l.label}`}
              onClick={() => onChange(l.value)}
              className={[
                "flex flex-col items-center justify-center rounded-lg border px-1.5 text-center transition-colors",
                big ? "min-h-[52px] py-2 sm:min-h-[64px]" : "min-h-[44px] py-1.5 sm:min-h-[52px]",
                on
                  ? words
                    ? "border-pme-navy bg-pme-navy text-white shadow-sm"
                    : "border-pme-blue bg-pme-blue text-white shadow-sm"
                  : words
                    ? "border-gray-200 bg-white text-gray-700 hover:border-pme-navy/40 hover:bg-pme-sky"
                    : "border-gray-200 bg-white text-gray-600 hover:border-pme-blue/50 hover:bg-pme-blue/5",
                disabled ? "cursor-not-allowed opacity-50" : "",
              ].join(" ")}
            >
              {words ? (
                <span className={big ? "text-sm font-semibold leading-tight" : "text-xs font-semibold leading-tight sm:text-sm"}>{l.label}</span>
              ) : (
                <>
                  <span className={big ? "text-lg font-bold leading-none" : "text-sm font-bold leading-none"}>{l.value}</span>
                  <span className={`mt-1 hidden text-[10px] leading-tight sm:block ${on ? "text-white/90" : "text-gray-500"}`}>{l.label}</span>
                </>
              )}
            </button>
          );
        })}
      </div>
      {chosen ? (
        <p className="mt-1.5 text-xs text-gray-600" aria-live="polite">
          <span className={words ? "font-semibold text-pme-navy" : "font-semibold text-pme-navy"}>
            {words ? chosen.label : `${chosen.value} · ${chosen.label}`}.
          </span>{" "}
          {chosen.hint}
        </p>
      ) : !words ? (
        <div className="mt-1 flex justify-between text-[11px] text-gray-400 sm:hidden" aria-hidden>
          <span>1 {levels[0].label}</span>
          <span>5 {levels[4].label}</span>
        </div>
      ) : null}
    </div>
  );
}
